import { useEffect, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { formatPrice } from "../../lib/utils";
import ProviderReview from "./ProviderReview";

const STATUS_VARIANT = { draft: "default", pending: "warning", needs_info: "warning", approved: "accent", suspended: "dark", rejected: "dark" };
const STATUS_LABEL = {
  draft: "Setting up — not live",
  pending: "Waiting for review",
  needs_info: "Information requested",
  approved: "Live",
  suspended: "Suspended",
  rejected: "Rejected",
};
const SUB_LABEL = { free: "Free plan", active: "Pro — paid", grace: "Pro — overdue", lapsed: "Pro — unpaid", exempt: "Pro — comped" };
const SUB_VARIANT = { free: "default", active: "accent", grace: "warning", lapsed: "warning", exempt: "accent" };
const HOLD_LABEL = { subscription: "Hidden — Pro unpaid", statement: "Hidden — statement unpaid" };

const FILTERS = [
  { value: "", label: "All" },
  { value: "review", label: "Needs review" },
  { value: "draft", label: "Setting up" },
  { value: "needs_info", label: "Info requested" },
  { value: "approved", label: "Live" },
  { value: "suspended", label: "Suspended" },
  { value: "rejected", label: "Rejected" },
  { value: "unpaid", label: "Hidden — unpaid" },
];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [reviewId, setReviewId] = useState(null);

  const load = () =>
    api
      .get("/admin/businesses", { params: { status: status || undefined, q: query || undefined } })
      .then(({ data }) => setBusinesses(data.data || []))
      .catch(() => setBusinesses([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const act = async (id, request) => {
    setBusyId(id);
    try {
      await request();
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const recordPayment = (b) => act(b.id, () => api.patch(`/admin/businesses/${b.id}/subscription`, { extend_days: 30 }));
  const markInvoicePaid = (b, invoice) => {
    const renews = invoice.type === "subscription" ? " This also renews SHUWA Pro for a month." : "";
    if (!window.confirm(`Record ${invoice.number} as paid (cash / bank transfer)?${renews}`)) return;
    act(b.id, () => api.patch(`/admin/invoices/${invoice.id}`, { status: "paid" }));
  };

  const setFree = (b) => {
    if (!window.confirm(`Move ${b.name} to the Free plan?`)) return;
    act(b.id, () => api.patch(`/admin/businesses/${b.id}/subscription`, { plan: "free" }));
  };

  return (
    <div>
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Businesses</h1>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`rounded-full border px-3 py-1.5 text-sm ${status === f.value ? "border-primary bg-primary text-white" : "border-border"}`}
          >
            {f.label}
          </button>
        ))}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="ml-auto w-full sm:w-64"
        >
          <Input placeholder="Search name or link" aria-label="Search businesses" value={query} onChange={(e) => setQuery(e.target.value)} />
        </form>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-3">
          {businesses.map((b) => {
            const sub = b.subscription || {};
            const busy = busyId === b.id;
            return (
              <Card key={b.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-semibold text-slate-900">
                    {b.name}
                    <a href={`/${b.slug}`} target="_blank" rel="noreferrer" className="text-xs font-normal text-primary">
                      /{b.slug}
                    </a>
                  </p>
                  <p className="break-words text-xs text-muted-foreground">
                    {b.owner?.full_name} · {b.owner?.email} · {b.category?.name}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant={STATUS_VARIANT[b.status]}>{STATUS_LABEL[b.status] || b.status}</Badge>
                    {b.billing_hold_at && <Badge variant="warning">{HOLD_LABEL[b.hold_reason] || "Hidden — unpaid"}</Badge>}
                    {b.is_verified && <Badge variant="primary">Verified</Badge>}
                    {b.status === "approved" && b.flagged_at && <Badge variant="warning">Flagged</Badge>}
                    {b.pending_verifications?.length > 0 && (
                      <Badge variant="warning">Documents to check: {b.pending_verifications.map((t) => t.replace(/_/g, " ")).join(", ")}</Badge>
                    )}
                    {b.paused_at && <Badge variant="default">Paused by owner</Badge>}
                    {sub.status && <Badge variant={SUB_VARIANT[sub.status]}>{SUB_LABEL[sub.status]}</Badge>}
                    {sub.expires_at && <span className="text-muted-foreground">Due {formatDate(sub.expires_at)}</span>}
                    {sub.auto_renew && sub.provider && <span className="text-muted-foreground">· Auto-renew on</span>}
                    {b.boost_enabled && <Badge variant="accent">Boost on</Badge>}
                    {b.owed_to_shuwa > 0 && (
                      <span className="font-medium text-amber-700">Owes SHUWA {formatPrice(b.owed_to_shuwa)}</span>
                    )}
                  </div>
                  {b.open_invoices?.map((invoice) => (
                    <div key={invoice.id} className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {invoice.type === "subscription" ? "Pro invoice" : "Statement"} {invoice.number} ·{formatPrice(invoice.amount)} · due {formatDate(invoice.due_at)}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => markInvoicePaid(b, invoice)}
                        className="font-semibold text-primary underline"
                      >
                        Mark paid
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={b.status === "pending" || b.flagged_at || b.pending_verifications?.length ? "primary" : "outline"}
                    disabled={busy}
                    onClick={() => setReviewId(b.id)}
                  >
                    <ClipboardCheck className="h-4 w-4" /> Review
                  </Button>
                  {sub.status !== "exempt" && (
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => recordPayment(b)} title="Record a cash or bank Pro payment">
                      +30 days Pro
                    </Button>
                  )}
                  {sub.plan === "pro" && (
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setFree(b)}>
                      Set Free
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
          {businesses.length === 0 && <p className="text-muted-foreground">No businesses found.</p>}
        </div>
      )}

      {reviewId && <ProviderReview businessId={reviewId} onClose={() => setReviewId(null)} onChanged={load} />}
    </div>
  );
}
